/**
 * Basic data structures used throughout the project.
 * */

import type {
  AbleitungsmodifikationenRepraesentation,
  AbleitungsmodifikationenStruktur,
  Anzahl,
  FreigabeStatus,
  xdf3,
} from "./xdatenfelder";

// Within a schema or group tree, each fim Id must only be used once,
// regardless of whether it is a version or arbeitskopie.
// Therefore, the version is not needed to uniquely identify a node.
export type SchemaIdentifier = {
  type: "schema";
  fimId: string;
};

export type GroupIdentifier = {
  type: "group";
  fimId: string;
};

export type FieldIdentifier = {
  type: "field";
  fimId: string;
};

export type RuleIdentifier = {
  type: "rule";
  fimId: string;
};

export type ElementIdentifier =
  | SchemaIdentifier
  | GroupIdentifier
  | FieldIdentifier
  | RuleIdentifier;

export type NodeIdentifier =
  | SchemaIdentifier
  | GroupIdentifier
  | FieldIdentifier;

export type ParentIdentifier = SchemaIdentifier | GroupIdentifier;
export type ChildIdentifier = GroupIdentifier | FieldIdentifier;

// ====================================================
// Types for paths within a schema tree.

/**
 * An absolute path to a specific element in the schema.
 *
 * Some constraints, that are enforced by this type:
 * - The first element is always the schema
 * - The last element is optionally a field
 * - All other elements must be groups
 * */
export type AbsoluteElementPath =
  | [SchemaIdentifier, ...GroupIdentifier[], FieldIdentifier]
  | [SchemaIdentifier, ...GroupIdentifier[]];

/**
 * An absolute path to a parent element (group or schema)
 * This is basically the same as `AbsoluteElementPath`, but with the extra condition that
 * the last element must be a group.
 *
 * This means that every AbsoluteParentPath is also a valid AbsoluteElementPath.
 * */
export type AbsoluteParentPath = [SchemaIdentifier, ...GroupIdentifier[]];

/**
 * A relative path to a specific element within a group or schema.
 *
 * In addition to valid absolute paths, this also allows paths starting with a group or
 * just a single field.
 */
export type RelativeElementPath =
  | AbsoluteElementPath
  | [...GroupIdentifier[], FieldIdentifier]
  | [...GroupIdentifier[]];

// ====================================================
// Basic identifiers for the different types of XDF elements.

export type Identifier = {
  fimId: string;
  fimVersion?: string;
};

export type VersionIdentifier = {
  fimId: string;
  fimVersion: string;
};

// ====================================================
// The actual data structures containing the element information
export type BasicData = {
  name: string;
  beschreibung: string | undefined;
  definition: string | undefined;
  normReferences: xdf3.NormReference[];
  releaseStatus: FreigabeStatus | undefined;
  statusGesetztAm: Date | undefined;
  statusGesetztDurch: string | undefined;
  gueltigAb: Date | undefined;
  gueltigBis: Date | undefined;
  versionshinweis: string | undefined;
  veroeffentlichungsdatum: Date | undefined;
  relations: xdf3.Relation[];
  keywords: xdf3.Keyword[];
};

/**
 * Import-related data structures.
 * These contain all of the data to import a full group or field, including all contained elements (groups, fields, rules, code lists).
 */

export type GroupImport = {
  rootGroupIdentifier: Identifier;
  rules: Rule[];
  fields: Field[];
  groups: Group[];
};

export type FieldImport = {
  field: Field;
  rules: Rule[];
};

/**
 * The new data structure containing all of the xdf information for an open document in the editor.
 * The document can either represent be a schema or a group, either an Arbeitskopie or a versioned (and therefore immutable) element.
 * */

export type SchemaDocument = {
  type: "schema";

  schema: Schema;
  groups: Record<string, Group>;
  fields: Record<string, Field>;
  rules: Record<string, Rule>;
};

export type GroupDocument = {
  type: "group";

  root: string;
  groups: Record<string, Group>;
  fields: Record<string, Field>;
  rules: Record<string, Rule>;
};

export type Document = SchemaDocument | GroupDocument;

/**
 * Rules must probably not be stored individually, but are part of a schema, group or field.
 * This design decision is backend by:
 * 1. In the database of the FIM Portal, every rule only ever has a single parent element.
 * 2. I have the explicit confirmation from Jessica Gettkandt, that rules are only ever re-used by re-using the parent element.
 * */
export type Rule = RuleVersion | RuleArbeitskopie;

export type RuleVersion = {
  type: "version";

  fimId: string;
  fimVersion: string;
  name: string;
  beschreibung: string | undefined;
  freitextRegel: string | undefined;
  bezug: xdf3.NormReference[];
  stichwort: xdf3.Keyword[];
  fachlicherErsteller: string | undefined;
  letzteAenderung: Date | undefined;

  ruleType: "valid";
  skript: string | undefined;
};
export type RuleArbeitskopie = {
  type: "arbeitskopie";

  fimId: string;
  name: string;
  beschreibung: string | undefined;
  freitextRegel: string | undefined;
  bezug: xdf3.NormReference[];
  stichwort: xdf3.Keyword[];
  fachlicherErsteller: string | undefined;
  letzteAenderung: Date | undefined;

  ruleType: "active" | "valid";
  skript: string | undefined;
  expression: RuleExpression | undefined;
  target: RelativeElementPath | undefined;
};

export type RuleReference = {
  fimId: string;
  fimVersion: string | undefined;
};

export type RuleVersionReference = {
  fimId: string;
  fimVersion: string;
};

export type Schema = SchemaArbeitskopie | SchemaVersion;

export type SchemaArbeitskopie = {
  type: "arbeitskopie";

  fimId: string;
  basicData: BasicData;

  bezeichnung: string | undefined;
  hilfeText: string | undefined;
  ableitungsModifikationenStrutkur: AbleitungsmodifikationenStruktur;
  ableitungsModifikationenRepraesentation: AbleitungsmodifikationenRepraesentation;
  documentSteckbriefFimId: string | undefined;

  children: Child[];
  rules: RuleReference[];
};

export type SchemaVersion = {
  type: "version";
  url: string;

  fimId: string;
  fimVersion: string;
  basicData: BasicData;

  bezeichnung: string | undefined;
  hilfeText: string | undefined;
  ableitungsModifikationenStrutkur: AbleitungsmodifikationenStruktur;
  ableitungsModifikationenRepraesentation: AbleitungsmodifikationenRepraesentation;
  documentSteckbriefFimId: string;

  children: VersionChild[];
  rules: RuleVersionReference[];
};

export type ArbeitskopieChild = {
  type: "field" | "group";
  element: Identifier;
  cardinality: Anzahl;
};

export type VersionChild = {
  type: "field" | "group";
  element: VersionIdentifier;
  cardinality: Anzahl;
};

export type Child = ArbeitskopieChild | VersionChild;

export type Group = GroupVersion | GroupArbeitskopie;

export type GroupVersion = {
  type: "version";
  url: string;

  namespace: string;
  fimId: string;
  fimVersion: string;

  basicData: BasicData;
  art: xdf3.Gruppenart | undefined;
  children: VersionChild[];
  rules: RuleVersionReference[];
};

export type GroupArbeitskopie = {
  type: "arbeitskopie";

  fimId: string;

  basicData: BasicData;
  art: xdf3.Gruppenart | undefined;
  children: Child[];
  rules: RuleReference[];
};

export type FieldConstraints = {
  minLength: number | undefined;
  maxLength: number | undefined;
  minValue: string | undefined;
  maxValue: string | undefined;
  pattern: string | undefined;
};

export type Field = FieldVersion | FieldArbeitskopie;

export type FieldVersion = {
  type: "version";
  url: string;

  namespace: string;
  fimId: string;
  fimVersion: string;
  basicData: BasicData;
  rules: RuleVersionReference[];
  value: string | undefined;
  inputType: xdf3.Feldart;
  dataType: xdf3.Datentyp;
  options: [string, string][];
  constraints: FieldConstraints;
};

export type FieldArbeitskopie = {
  type: "arbeitskopie";

  fimId: string;
  basicData: BasicData;
  inputType: xdf3.Feldart;
  dataType: xdf3.Datentyp;
  value: string | undefined;
  rules: RuleReference[];
  options: [string, string][];
  constraints: FieldConstraints;
};

// ====================================================
// Rule expressions
// Note: These are basically similar to what we want in the schema, but with a little
// more flexibility to allow empty children commonly present during editing.

export type RuleExpression = RuleOperation | RuleVariable | string | boolean;

export type RuleOperation = {
  type: "operation";
  operation: string;
  operands: Array<RuleExpression | null>;
};

export type RuleVariable = {
  type: "identifier";
  identifier: string | null;
};

class AssertionError extends Error {}

export function assert(condition: any, message?: string): asserts condition {
  if (!condition) {
    throw new AssertionError(message);
  }
}
